import React, { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, Filter, Calendar as CalendarIcon, X, Download, Palette, Moon, Sun } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { exportAnalyticsData, exportUserData, exportCouponData } from '@/utils/adminExport';

interface FilterOption {
  label: string;
  value: string;
  category?: string;
}

interface DateRange {
  from?: Date;
  to?: Date;
}

interface AdminSearchFilterProps {
  onSearch: (query: string) => void;
  onFilter: (filters: Record<string, any>) => void;
  filterOptions: FilterOption[];
  exportData?: any[];
  exportType?: 'analytics' | 'users' | 'coupons';
  placeholder?: string;
  className?: string;
}

export function AdminSearchFilter({
  onSearch,
  onFilter,
  filterOptions,
  exportData,
  exportType,
  placeholder = "Search admin data...",
  className = ""
}: AdminSearchFilterProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [dateRange, setDateRange] = useState<DateRange>({});
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const { theme, toggleTheme, setThemeColor, isCustomizing, setIsCustomizing } = useAdminTheme();

  // Group filter options by category
  const groupedFilters = useMemo(() => {
    const grouped: Record<string, FilterOption[]> = {};
    if (Array.isArray(filterOptions)) {
      filterOptions.forEach(option => {
        const category = option.category || 'General';
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(option);
      });
    }
    return grouped;
  }, [filterOptions]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    onSearch(value);
  };

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...activeFilters };
    if (value === 'all' || value === '' || value === null) {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }
    setActiveFilters(newFilters);
    onFilter({ ...newFilters, dateRange });
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    onFilter({ ...activeFilters, dateRange: range });
  };

  const clearFilters = () => {
    setActiveFilters({});
    setDateRange({});
    setSearchQuery('');
    onSearch('');
    onFilter({});
  };

  const handleExport = (format: 'csv' | 'json' | 'excel' | 'pdf') => {
    if (!exportData || !exportType) return;
    
    switch (exportType) {
      case 'analytics':
        exportAnalyticsData(exportData, format);
        break;
      case 'users':
        exportUserData(exportData, format as 'csv' | 'json' | 'excel');
        break;
      case 'coupons':
        exportCouponData(exportData, format as 'csv' | 'json' | 'excel');
        break;
    }
  };

  const colorOptions = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', 
    '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'
  ];

  return (
    <Card className={cn("w-full", className)}>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <CardTitle className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            Search & Filter
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Theme Controls */}
            <Button
              variant="outline"
              size="sm"
              onClick={toggleTheme}
              className="gap-2"
            >
              {theme.mode === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span className="hidden sm:inline">
                {theme.mode === 'dark' ? 'Light' : 'Dark'}
              </span>
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsCustomizing(!isCustomizing)}
              className="gap-2"
            >
              <Palette className="h-4 w-4" />
              <span className="hidden sm:inline">Theme</span>
            </Button>

            {/* Export Menu */}
            {exportData && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Download className="h-4 w-4" />
                    <span className="hidden sm:inline">Export</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48" align="end">
                  <div className="grid gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleExport('csv')}>
                      Export as CSV
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleExport('json')}>
                      Export as JSON
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleExport('excel')}>
                      Export as Excel
                    </Button>
                    {exportType === 'analytics' && (
                      <Button variant="ghost" size="sm" onClick={() => handleExport('pdf')}>
                        Export as PDF
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        {/* Theme Color Picker */}
        {isCustomizing && (
          <div className="flex items-center gap-2 mt-3 p-3 bg-muted rounded-lg">
            <span className="text-sm font-medium">Theme Color:</span>
            <div className="flex gap-2">
              {colorOptions.map(color => (
                <button
                  key={color}
                  className={cn(
                    "w-6 h-6 rounded-full border-2 transition-all",
                    theme.primaryColor === color ? "border-black scale-110" : "border-gray-300"
                  )}
                  style={{ backgroundColor: color }}
                  onClick={() => setThemeColor(color)}
                />
              ))}
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={placeholder}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Quick Filters */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="gap-2"
          >
            <Filter className="h-4 w-4" />
            Filters
          </Button>

          {/* Date Range Picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "gap-2 justify-start text-left font-normal",
                  !dateRange.from && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="h-4 w-4" />
                {dateRange.from ? (
                  dateRange.to ? (
                    `${format(dateRange.from, "MMM dd")} - ${format(dateRange.to, "MMM dd")}`
                  ) : (
                    format(dateRange.from, "MMM dd, yyyy")
                  )
                ) : (
                  "Date range"
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange.from}
                selected={dateRange as any}
                onSelect={handleDateRangeChange as any}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>

          {/* Clear Filters */}
          {(Object.keys(activeFilters).length > 0 || searchQuery || dateRange.from) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="gap-2 text-destructive"
            >
              <X className="h-4 w-4" />
              Clear All
            </Button>
          )}
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-3 bg-muted rounded-lg">
            {Object.entries(groupedFilters).map(([category, options]) => (
              <div key={category}>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  {category}
                </label>
                <Select
                  value={activeFilters[category.toLowerCase()] || 'all'}
                  onValueChange={(value) => handleFilterChange(category.toLowerCase(), value)}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder={`All ${category}`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All {category}</SelectItem>
                    {options.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}

        {/* Active Filter Badges */}
        {Object.keys(activeFilters).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(activeFilters).map(([key, value]) => (
              <Badge
                key={key}
                variant="secondary"
                className="gap-1 pr-1"
              >
                {key}: {value}
                <button
                  onClick={() => handleFilterChange(key, null)}
                  className="ml-1 hover:bg-muted-foreground/20 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}